/**
 * StrategyRenameModal Component
 *
 * Simple modal for renaming an existing strategy.
 * Pre-fills with current name, validates non-empty.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Services & Types
import { strategyService } from '@/services/StrategyService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { Strategy } from '@/types/schema/Strategy';

/**
 * Props for StrategyRenameModal
 */
export interface StrategyRenameModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Strategy to rename */
  strategy: Strategy;
  /** Callback after successful save */
  onSave: () => void;
}

/**
 * StrategyRenameModal Component
 */
export const StrategyRenameModal: React.FC<StrategyRenameModalProps> = ({
  isOpen,
  onClose,
  strategy,
  onSave,
}) => {
  // Form state
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Reset form when modal opens or strategy changes
  useEffect(() => {
    if (isOpen) {
      setName(strategy.name);
      setNameError(null);
      setSubmitError(null);
    }
  }, [isOpen, strategy.name]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError('Name is required');
      return false;
    }
    setNameError(null);
    return true;
  };

  /**
   * Handle name change
   */
  const handleNameChange = (value: string) => {
    setName(value);
    if (nameError) {
      setNameError(null);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    const trimmedName = name.trim();

    // Skip if name unchanged
    if (trimmedName === strategy.name) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);

      await strategyService.update(strategy.id, { name: trimmedName });
      syncCoordinatorService.notifyChange('strategies', strategy.id);

      showToast({
        title: 'Strategy Renamed',
        description: `Strategy renamed to "${trimmedName}"`,
        color: 'success',
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to rename strategy:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to rename strategy';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      placement="top-center"
    >
      <ModalContent>
        <ModalHeader>Rename Strategy</ModalHeader>

        <ModalBody className="gap-4">
          {/* Name */}
          <Input
            label="Name"
            placeholder="Enter strategy name"
            value={name}
            onValueChange={handleNameChange}
            isRequired
            isInvalid={!!nameError}
            errorMessage={nameError}
            autoFocus
            className="[&_input]:text-base"
          />

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StrategyRenameModal;
